import { Maximize2, Minimize2 } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DataTableToolbar } from '../DimeDataTable/DataTableToolbar.js';
import type { ToolbarTable } from '../DimeDataTable/toolbar-table.js';
import { DimeFilterButtonContent } from '../DimeFilterButtonContent.js';
import { isExpandAllDisabled } from '../lib/is-expand-all-disabled.js';
import { NO_EXPANDABLE_ROWS_TOOLTIP } from '../lib/no-expandable-rows-tooltip.js';

interface ExpandableTableToolbarProps {
  table: ToolbarTable;
  searchColumn?: string;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  showViewOptions?: boolean;
  showExpandAll?: boolean;
  anyRowExpanded?: boolean;
  hasExpandableRows?: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  filterChips?: React.ReactNode;
}

/** DimeTreeTable's toolbar: DataTableToolbar plus an expand/collapse-all toggle ahead of `actions`. */
export function ExpandableTableToolbar({
  table,
  searchColumn,
  searchPlaceholder,
  searchMaxLength,
  onSearchChange,
  searchValue,
  filters,
  actions,
  showViewOptions,
  showExpandAll,
  anyRowExpanded = false,
  hasExpandableRows = true,
  onExpandAll,
  onCollapseAll,
  filterChips,
}: Readonly<ExpandableTableToolbarProps>) {
  const expandAllDisabled = isExpandAllDisabled({ anyRowExpanded, hasExpandableRows });

  return (
    <DataTableToolbar
      table={table}
      searchColumn={searchColumn}
      searchPlaceholder={searchPlaceholder}
      searchMaxLength={searchMaxLength}
      onSearchChange={onSearchChange}
      searchValue={searchValue}
      filters={filters}
      showViewOptions={showViewOptions}
      filterChips={filterChips}
      actions={
        <>
          {showExpandAll ? (
            <DimeButton
              variant="outline"
              size="sm"
              // Square once the label hides below sm, like the Export button.
              className="gap-1.5 max-sm:size-7 max-sm:p-0"
              disabled={expandAllDisabled}
              disabledTooltip={expandAllDisabled ? NO_EXPANDABLE_ROWS_TOOLTIP : undefined}
              aria-label={anyRowExpanded ? 'Collapse' : 'Expand'}
              onClick={anyRowExpanded ? onCollapseAll : onExpandAll}
            >
              <DimeFilterButtonContent
                label={anyRowExpanded ? 'Collapse' : 'Expand'}
                icon={
                  anyRowExpanded ? (
                    <Minimize2 strokeWidth={1.75} aria-hidden />
                  ) : (
                    <Maximize2 strokeWidth={1.75} aria-hidden />
                  )
                }
              />
            </DimeButton>
          ) : null}
          {actions}
        </>
      }
    />
  );
}
